/**
 * Product services
 * Handles pass-through product queries, stock, pricing rule lookups, and inventory.
 */
import axios from "axios";
import { applyAuthInterceptors } from "./authInterceptor";
import { applyHealthInterceptors } from "../utils/healthReporter";
import { buildConsentQuery, buildSetConsentMutation, readConsent } from "../utils/fashion/consent";
import { buildMarkShipToHomeMutation } from "../utils/fashion/shipTo";
import { readMessage } from "../utils/fashion/holds";

const retailURL = process.env.REACT_APP_serverUrl;
const retailInstance = axios.create();
retailInstance.defaults.baseURL = retailURL;
retailInstance.defaults.method = "POST";
retailInstance.defaults.headers.post["Content-Type"] = "application/json";

// Dynamic tenant baseURL interceptor
retailInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_serverUrl;
  if (url) config.baseURL = url;
  return config;
});

// Auth interceptors: token attachment + 401 refresh/retry
applyAuthInterceptors(retailInstance);

// Health tracking interceptors (latency + success/failure)
applyHealthInterceptors(retailInstance);

const getProductsNearbyStockService = async (data) => {
  try {
    const response = await retailInstance({ data });
    return response;
  } catch (error) {
    console.error("Error in getProductsNearbyStock:", error);
    return null;
  }
};

// Fashion layout store operations (markdowns, size holds, alterations). Returns
// the response, GraphQL errors included, or null when the call itself failed.
const fashionPosService = async (data) => {
  try {
    const response = await retailInstance({ data });
    return response;
  } catch (error) {
    console.error("Error in fashionPosService:", error);
    return null;
  }
};

// WhatsApp offers consent and ship to home (fashion layout). Built with GraphQL
// variables (utils/fashion/consent, shipTo); each returns the parsed result
// and never throws.
const getFashionConsent = async (customerId) => readConsent(await fashionPosService(buildConsentQuery(customerId)));

const setFashionConsent = async (customerId, bunitId, whatsappOffers) =>
  readMessage(await fashionPosService(buildSetConsentMutation(customerId, bunitId, whatsappOffers)), "setFashionConsent", "Couldn't save it. Try again.");

const markFashionShipToHome = async (orderId, bunitId, address) =>
  readMessage(await fashionPosService(buildMarkShipToHomeMutation(orderId, bunitId, address)), "markFashionShipToHome", "Couldn't mark the bill to ship. Try again.");

const generateTransferRequestService = async (data) => {
  try {
    const response = await retailInstance({ data });
    return response;
  } catch (error) {
    console.error("Error in generateTransferRequest:", error);
    return null;
  }
};

const getStockForProductService = async (data) => {
  try {
    const response = await retailInstance({ data });
    return response;
  } catch (error) {
    console.error("Error in getStockForProduct:", error);
    return null;
  }
};

const getUoms = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get uom");
  }
};

const getPosSaletypes = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get pos sale type");
  }
};

const getProductCategory = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in product category");
  }
};

const getPricingRule = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get priding rules");
  }
};

const getProducts = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get products");
  }
};

const getPricingPolicyService = async (data) => {
  try {
    const response = await retailInstance({ data });
    return response;
  } catch (error) {
    console.error("Error in get pricing policy:", error);
    return null;
  }
};

const completePurchaseOrder = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in complete purchase order");
  }
};

export {
  getProductsNearbyStockService,
  generateTransferRequestService,
  fashionPosService,
  getFashionConsent,
  setFashionConsent,
  markFashionShipToHome,
  getStockForProductService,
  getUoms,
  getPosSaletypes,
  getProductCategory,
  getPricingRule,
  getProducts,
  completePurchaseOrder,
  getPricingPolicyService,
};
